import { useEffect, useMemo, useState } from 'react';
import { useStores } from '@/hooks/use-stores';
import { useMeasurement } from '@/hooks/useMeasurement';
import { EngineConfigStatus } from '@/store/Engine/constants';
import { FallBackdata } from './types';
import {
  appliedCompressorsValues,
  compressorNameFromID,
  getCompData,
  getMetricsByCompressor,
  isFlowKPIFailed,
  isPressureKPIFailed,
  transformNewFallbackList,
} from './Fallbacks.utils';
import { MetricSetpoint, Setpoint } from '@/store/Engine/common';

export const useFallbackData = (
  configurationStatus: EngineConfigStatus,
  fromVersionHistory: boolean,
  versionNumber: string,
) => {
  const { engineStore, devicesStore, proactiveEngineStore, stationStore } = useStores();
  const { tvalue } = useMeasurement();

  const [selectedId, setSelectedId] = useState<number>(1);
  const [appliedFilters, setAppliedFilters] = useState([]);
  const [searchValue, setSearchValue] = useState<string>('');
  const [inputValue, setInputValue] = useState<string>('');
  const [editableTableData, setEditableTableData] = useState<Setpoint[]>([]);
  const [tableName, setTableName] = useState<string>('');

  const compressors = devicesStore.compressors;

  useEffect(() => {
    engineStore.pagination.page = 1;
  }, [configurationStatus]);

  useEffect(() => {
    const compressors = appliedCompressorsValues(appliedFilters);
    const flowKPIFailed = isFlowKPIFailed(appliedFilters);
    const pressureKPIFailed = isPressureKPIFailed(appliedFilters);
    let page = engineStore.pagination.page;
    if (appliedFilters.length > 0 || searchValue) {
      page = 1;
    }
    engineStore.getFallbackScenariosByVersion(
      versionNumber,
      fromVersionHistory,
      configurationStatus,
      page,
      engineStore.pagination.pageSize,
      compressors,
      flowKPIFailed,
      pressureKPIFailed,
      tvalue,
      searchValue,
    );
  }, [
    configurationStatus,
    appliedFilters,
    engineStore.pagination.page,
    engineStore.pagination.pageSize,
    fromVersionHistory,
    versionNumber,
    searchValue,
  ]);

  useEffect(() => {
    if (stationStore.currentStation?.id) {
      proactiveEngineStore.getCommandsHistory(false);
    }
  }, [stationStore.currentStation]);

  const newFallBackList: FallBackdata[] = useMemo(() => {
    return transformNewFallbackList(
      engineStore.newFallbackScenarios,
      proactiveEngineStore.commandsHistory,
      proactiveEngineStore.proactiveEngineState,
      fromVersionHistory,
    );
  }, [
    engineStore.newFallbackScenarios,
    proactiveEngineStore.commandsHistory,
    fromVersionHistory,
    versionNumber,
    proactiveEngineStore.proactiveEngineState,
  ]);

  const selectedFallback = newFallBackList.find(f => f.id === selectedId);

  const compData = useMemo(() => {
    return getCompData(selectedFallback, engineStore, devicesStore?.sortedCompressors);
  }, [selectedFallback, engineStore?.setpointsConfiguration, devicesStore?.sortedCompressors]);

  useEffect(() => {
    if (compData.length > 0) {
      setTableName(compData[0]?.name || '');
    }
  }, [compData]);

  const currentTableData: Setpoint[] = useMemo(() => {
    const setpoints: Setpoint[] = editableTableData.length > 0 ? editableTableData : compData;
    return setpoints.map(
      setpoint =>
        ({
          ...setpoint,
          compressorName: setpoint.compressorName || compressorNameFromID(setpoint.compressorId, compressors),
        } as Setpoint),
    );
  }, [editableTableData, compData]);

  const filteredList = useMemo(() => {
    return newFallBackList;
  }, [newFallBackList]);

  const validateSearchValue = (value: string) => {
    if (!value) {
      return true;
    }
    return /^[0-9]+$/.test(value);
  };

  useEffect(() => {
    if (filteredList.length > 0 && !filteredList.find(f => f.id === selectedId)) {
      setSelectedId(filteredList[0].id);
    }
  }, [filteredList, selectedId]);

  useEffect(() => {
    setEditableTableData([]);
  }, [selectedId]);

  const engineMetricSetpoints: MetricSetpoint[] = useMemo(
    () => engineStore.engineMetricSetpoints,
    [engineStore.engineMetricSetpoints],
  );

  const metricsByCompressor = useMemo(() => getMetricsByCompressor(engineMetricSetpoints), [engineMetricSetpoints]);

  const handleCellChange = (key: string | number, field: keyof Setpoint, value) => {
    setEditableTableData((prev: Setpoint[]): Setpoint[] => {
      const baseData = prev.length > 0 ? prev : compData;
      return baseData.map(row => (row.key === key ? ({ ...row, [field]: value } as Setpoint) : row));
    });
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
    if (!e.target.value) {
      setSearchValue('');
    }
  };

  const handleSearchSubmit = () => {
    if (validateSearchValue(inputValue)) {
      setSearchValue(inputValue);
    }
  };

  const handleSearchClear = () => {
    setSearchValue('');
    setInputValue('');
  };

  const handleSearchIconClick = () => {
    if (validateSearchValue(inputValue)) {
      setSearchValue(inputValue);
    }
  };

  const handleFilterApply = filters => {
    setAppliedFilters(filters);
  };

  const handleAppliedFiltersClose = () => {
    setAppliedFilters([]);
  };

  const handleListItemClick = (item: FallBackdata) => {
    setSelectedId(item.id);
  };

  const handleViewButtonClick = (e: React.MouseEvent, item: FallBackdata) => {
    e.stopPropagation();
    setSelectedId(item.id);
  };

  const handlePaginationChange = (page: number, pageSize: number) => {
    const compressors = appliedCompressorsValues(appliedFilters);
    const flowKPIFailed = isFlowKPIFailed(appliedFilters);
    const pressureKPIFailed = isPressureKPIFailed(appliedFilters);

    engineStore.getFallbackScenariosByVersion(
      versionNumber,
      fromVersionHistory,
      configurationStatus,
      page,
      pageSize,
      compressors,
      flowKPIFailed,
      pressureKPIFailed,
      tvalue,
    );
  };

  return {
    newFallBackList,
    selectedFallback,
    currentTableData,
    filteredList,
    compData,
    compressors,
    tableName,
    metricsByCompressor,

    selectedId,
    appliedFilters,
    searchValue,
    inputValue,
    editableTableData,

    handleCellChange,
    handleInputChange,
    handleSearchSubmit,
    handleSearchClear,
    handleSearchIconClick,
    handleFilterApply,
    handleAppliedFiltersClose,
    handleListItemClick,
    handleViewButtonClick,
    handlePaginationChange,
    setEditableTableData,
  };
};
